import { Message } from 'element-ui'
import { getToken, removeToken } from './auth'

let activeMessage = ''
const baseUrl = process.env.VUE_APP_API_BASE_URL || '/api'

function buildRequestUrl(url) {
  if (/^https?:\/\//.test(url)) {
    return url
  }

  if (baseUrl === '/api' && url.startsWith('/api/')) {
    return url
  }

  return `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`
}

function buildQueryString(params = {}) {
  const searchParams = new URLSearchParams()

  Object.keys(params).forEach(key => {
    const value = params[key]
    if (value === undefined || value === null || value === '') {
      return
    }
    searchParams.append(key, value)
  })

  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

function showErrorMessage(message) {
  if (!message || message === activeMessage) {
    return
  }

  activeMessage = message
  Message({
    message,
    type: 'error',
    duration: 3000,
    onClose: () => {
      if (activeMessage === message) {
        activeMessage = ''
      }
    }
  })
}

function buildRequestError(message, extra = {}) {
  const error = new Error(message)
  Object.keys(extra).forEach(key => {
    error[key] = extra[key]
  })
  return error
}

function isWrappedResponse(result) {
  return result && typeof result === 'object' && Object.prototype.hasOwnProperty.call(result, 'code')
}

function unwrapBusinessResponse(result) {
  if (!isWrappedResponse(result)) {
    return result
  }

  const code = Number(result.code)
  if (!Number.isNaN(code) && (code < 200 || code >= 300)) {
    throw buildRequestError(result.message || '请求失败', {
      businessCode: code,
      payload: result
    })
  }

  const message = String(result.message || '')
  if (/requested route does not exist/i.test(message)) {
    throw buildRequestError('接口未配置', {
      businessCode: code,
      payload: result,
      missingRoute: true
    })
  }

  if (Object.prototype.hasOwnProperty.call(result, 'data')) {
    return result.data
  }

  return result
}

async function parseResponseData(response) {
  const text = await response.text()
  if (!text) {
    return {}
  }

  const contentType = response.headers.get('content-type') || ''
  const isJsonResponse = contentType.includes('application/json')

  if (isJsonResponse) {
    try {
      return JSON.parse(text)
    } catch (error) {
      throw new Error('接口返回的 JSON 格式不正确')
    }
  }

  if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
    throw new Error('接口返回了 HTML 内容，请确认服务地址和环境变量配置正确')
  }

  throw new Error(text)
}

export default async function request(url, options = {}) {
  const headers = {
    ...(options.headers || {})
  }
  const token = getToken()

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  let body = options.body
  if (body && !(body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(body)
  }

  let response
  const requestUrl = `${buildRequestUrl(url)}${buildQueryString(options.params)}`

  try {
    response = await fetch(requestUrl, {
      method: options.method || 'GET',
      headers,
      body
    })
  } catch (error) {
    const networkError = buildRequestError('网络请求失败，请确认后端服务已启动', {
      isNetworkError: true
    })
    if (!options.suppressErrorMessage) {
      showErrorMessage(networkError.message)
    }
    throw networkError
  }

  if (options.responseType === 'blob') {
    if (!response.ok) {
      throw buildRequestError('请求失败', {
        status: response.status
      })
    }
    return response.blob()
  }

  const result = await parseResponseData(response)

  if (!response.ok) {
    if (response.status === 401) {
      removeToken()
    }

    if (result && typeof result === 'object') {
      const error = buildRequestError(result.message || '请求失败', {
        status: response.status,
        payload: result
      })
      if (!options.suppressErrorMessage) {
        showErrorMessage(error.message)
      }
      throw error
    }

    const error = buildRequestError(result || '请求失败', {
      status: response.status,
      payload: result
    })
    if (!options.suppressErrorMessage) {
      showErrorMessage(error.message)
    }
    throw error
  }

  return unwrapBusinessResponse(result)
}
